import React from 'react';
import { WBFlex, WBIconButton, WBTypography } from '@admiin-com/ds-web';
import { gql, useMutation } from '@apollo/client';
import { updateDocumentAnalysis } from '@admiin-com/ds-graphql';
import { useTranslation } from 'react-i18next';
import { alpha } from '@mui/material';

const UPDATE_DOCUMENT_ANALYSIS_FEEDBACK = gql(updateDocumentAnalysis);

interface ScanFeedbackProps {
  analysisId?: string | null;
  initialValue?: boolean | null;
}

export const ScanFeedback = ({
  analysisId,
  initialValue = null,
}: ScanFeedbackProps) => {
  const { t } = useTranslation();
  const [updateAnalysisFeedback, { loading }] = useMutation(
    UPDATE_DOCUMENT_ANALYSIS_FEEDBACK
  );
  const [isCorrect, setIsCorrect] = React.useState<boolean | null>(
    initialValue
  );

  const handleFeedback = async (value: boolean) => {
    setIsCorrect(value);
    try {
      if (analysisId) {
        await updateAnalysisFeedback({
          variables: {
            input: {
              id: analysisId,
              isCorrect: value,
            },
          },
        });
      }
    } catch (err) {
      console.error('Error updating document analysis feedback', err);
      // Revert the state if the update failed
      setIsCorrect(initialValue);
    }
  };

  if (!analysisId) {
    return null;
  }

  return (
    <WBFlex
      gap={1.5}
      alignItems="center"
      justifyContent="center"
      sx={{
        py: 1.5,
        px: 2,
        mt: 2,
        backgroundColor: 'background.paper',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        width: '100%',
      }}
    >
      <WBTypography
        variant="body2"
        sx={{
          fontWeight: 500,
          color: 'text.secondary',
          fontSize: '0.875rem',
        }}
      >
        {t('scanCorrectQuestion', { ns: 'taskbox' })}
      </WBTypography>

      <WBFlex gap={0.5} alignItems="center">
        <WBIconButton
          type="button"
          icon="ThumbsUp"
          size="small"
          color={isCorrect === true ? 'primary' : 'primary'}
          onClick={() => handleFeedback(true)}
          aria-label={t('thumbsUp', { ns: 'taskbox' })}
          disabled={loading}
          sx={{
            width: 32,
            height: 32,
            '&:hover': (theme) => ({
              backgroundColor:
                isCorrect === true
                  ? alpha(theme.palette.primary.main, 0.2)
                  : alpha(theme.palette.primary.main, 0.05),
            }),
            transition: 'background-color 0.15s ease-in-out',
            backgroundColor:
              isCorrect === true ? 'primary.main' : 'transparent',
            color:
              isCorrect === true ? 'primary.contrastText' : 'text.secondary',
            border: '1px solid',
            borderColor: isCorrect === true ? 'primary.main' : 'divider',
          }}
        />
        <WBIconButton
          type="button"
          icon="ThumbsDown"
          size="small"
          color={isCorrect === false ? 'error' : 'primary'}
          onClick={() => handleFeedback(false)}
          aria-label={t('thumbsDown', { ns: 'taskbox' })}
          disabled={loading}
          sx={{
            width: 32,
            height: 32,
            '&:hover': (theme) => ({
              backgroundColor:
                isCorrect === false
                  ? alpha(theme.palette.error.main, 0.2)
                  : alpha(theme.palette.error.main, 0.05),
            }),
            transition: 'background-color 0.15s ease-in-out',
            backgroundColor: isCorrect === false ? 'error.main' : 'transparent',
            color:
              isCorrect === false ? 'error.contrastText' : 'text.secondary',
            border: '1px solid',
            borderColor: isCorrect === false ? 'error.main' : 'divider',
          }}
        />
      </WBFlex>
    </WBFlex>
  );
};
